<template>
  <div>
    <div class="row mb-3">
      <div class="col-md-12">
        <div class="alert alert-info" v-if="solicitud.estado === 'EN_CONTRATACION'">
          <i class="fa fa-info-circle me-2"></i>
          La contratación ha sido adjudicada. Proceda a generar el contrato u orden correspondiente.
        </div>
        <div class="alert alert-success" v-else-if="contratos.length > 0" >
          <i class="fa fa-check-circle me-2"></i>
          <strong>Contrato/Orden generado</strong> - El documento formal ha sido registrado.
        </div>
      </div>
    </div>

    <!-- Formulario de contrato -->
    <div class="row" v-if="solicitud.estado === 'EN_CONTRATACION'">
      <div class="col-md-12">
        <div class="card">
          <div class="card-header bg-light"><h6 class="mb-0">Generar Contrato / Orden</h6></div>
          <div class="card-body">
            <div class="row">
              <div class="col-md-4">
                <div class="form-group mb-2">
                  <label class="form-label">Tipo Documento *</label>
                  <select v-model="contrato.tipo_documento" class="form-select" required>
                    <option value="">Seleccionar</option>
                    <option value="ORDEN_COMPRA">Orden de Compra (Bienes)</option>
                    <option value="ORDEN_SERVICIO">Orden de Servicio</option>
                    <option value="CONTRATO_ADMINISTRATIVO">Contrato Administrativo</option>
                  </select>
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-group mb-2">
                  <label class="form-label">N° Documento</label>
                  <input v-model="contrato.numero_documento" type="text" class="form-control" placeholder="Dejar vacío para auto-generar" readonly>
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-group mb-2">
                  <label class="form-label">Fecha Firma *</label>
                  <input v-model="contrato.fecha_firma" type="date" class="form-control" required readonly>
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-group mb-2">
                  <label class="form-label">Monto Total *</label>
                  <input v-model="contrato.monto_total" type="number" step="0.01" class="form-control" required readonly>
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-group mb-2">
                  <label class="form-label">Plazo (días)</label>
                  <input v-model="contrato.plazo_dias" type="number" class="form-control" readonly>
                </div>
              </div>
              <div class="col-md-4">
                <div class="form-group mb-2">
                  <label class="form-label">Lugar Ejecución</label>
                  <input v-model="contrato.lugar_ejecucion" type="text" class="form-control" readonly>
                </div>
              </div>
              <!--
              <div class="col-md-12">
                <div class="form-group mb-2">
                  <label class="form-label">Archivo Contrato/Orden (PDF) *</label>
                  <input type="file" @change="handleFileUpload" class="form-control" accept=".pdf">
                </div>
              </div>
              -->
              <div class="col-md-12 mt-2">
                <BButton variant="primary" @click="guardarContrato" :disabled="loading">
                  <i class="fa fa-save me-1"></i> Generar Contrato/Orden
                </BButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Lista de contratos generados -->
    <div class="row mt-3" v-if="contratos.length > 0">
      <div class="col-md-12">
        <div class="card">
          <div class="card-header bg-light"><h6 class="mb-0">Documentos Generados</h6></div>
          <div class="card-body">
            <div class="table-responsive">
              <table class="table table-sm table-bordered">
                <thead>
                  <tr><th>Tipo</th><th>N° Documento</th><th>Monto</th><th>Fecha Firma</th><th>Archivo</th></tr>
                </thead>
                <tbody>
                  <tr v-for="doc in contratos" :key="doc.id">
                    <td>{{ formatTipoDocumento(doc.tipo_documento) }}</td>
                    <td>{{ doc.numero_documento }}</td>
                    <td class="text-end">Bs. {{ formatNumber(doc.monto_total) }}</td>
                    <td>{{ formatDate(doc.fecha_firma) }}</td>
                    <td class="text-center">
                      <!-- Botón para generar PDF según el tipo de documento -->
                      <BButton 
                        v-if="doc.tipo_documento === 'ORDEN_COMPRA'"
                        variant="danger" 
                        size="sm"
                        @click="generarPdf(doc.id, 'orden-compra')"
                        :disabled="generandoPdf"
                        class="me-1"
                        title="Generar Orden de Compra PDF"
                      >
                        <i v-if="generandoPdf" class="fa fa-spinner fa-spin"></i>
                        <i v-else class="fa fa-file-pdf"></i> PDF OC
                      </BButton>
                      <BButton 
                        v-if="doc.tipo_documento === 'ORDEN_SERVICIO'"
                        variant="danger" 
                        size="sm"
                        @click="generarPdf(doc.id, 'orden-servicio')"
                        :disabled="generandoPdf"
                        class="me-1"
                        title="Generar Orden de Servicio PDF"
                      >
                        <i v-if="generandoPdf" class="fa fa-spinner fa-spin"></i>
                        <i v-else class="fa fa-file-pdf"></i> PDF OS
                      </BButton>
                      <span v-if="doc.tipo_documento === 'CONTRATO_ADMINISTRATIVO'" class="text-muted">
                        <i class="fa fa-file-pdf-o"></i> Pendiente
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import axios from 'axios';
import Swal from 'sweetalert2';
import { BButton } from 'bootstrap-vue-3';

const props = defineProps({ solicitud: { type: Object, required: true } });
const emit = defineEmits(['refresh']);

const loading = ref(false);
const generandoPdf = ref(false);
const archivoSeleccionado = ref(null);
const contratos = ref([]);

const contrato = ref({
  tipo_documento: '',
  numero_documento: '',
  monto_total: props.solicitud.monto_estimado,
  plazo_dias: props.solicitud.plazo_entrega_dias,
  lugar_ejecucion: props.solicitud.lugar_entrega,
  fecha_firma: new Date().toISOString().split('T')[0]
});

const fetchContratos = async () => {
  try {
    const response = await axios.get(`/contrato-orden/${props.solicitud.id}`);
    contratos.value = response.data.data;
  } catch (error) { console.error(error); }
};

const handleFileUpload = (event) => { archivoSeleccionado.value = event.target.files[0]; };

const guardarContrato = async () => {
  if (!contrato.value.tipo_documento) {
    Swal.fire('Error', 'Debe seleccionar el tipo de documento', 'error');
    return;
  }
  /*
  if (!archivoSeleccionado.value) {
    Swal.fire('Error', 'Debe seleccionar el archivo del contrato', 'error');
    return;
  }
  */
  loading.value = true;
  try {
    const formData = new FormData();
    formData.append('id_solicitud', props.solicitud.id);
    formData.append('tipo_documento', contrato.value.tipo_documento);
    formData.append('numero_documento', contrato.value.numero_documento);
    formData.append('monto_total', contrato.value.monto_total);
    formData.append('plazo_dias', contrato.value.plazo_dias);
    formData.append('lugar_ejecucion', contrato.value.lugar_ejecucion);
    formData.append('fecha_firma', contrato.value.fecha_firma);
    formData.append('archivo', archivoSeleccionado.value);

    await axios.post('/contrato-orden/guardar', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    Swal.fire('Éxito', 'Contrato/Orden generado correctamente', 'success');
    fetchContratos();
    emit('refresh');
  } catch (error) {
    Swal.fire('Error', error.response?.data?.message || 'Error al guardar', 'error');
  } finally { loading.value = false; }
};
/**
 * Generar PDF del documento (Orden de Compra u Orden de Servicio)
 * @param {number} contratoId - ID del contrato/orden
 * @param {string} tipo - 'orden-compra' o 'orden-servicio'
 */
const generarPdf = async (contratoId, tipo) => {
  generandoPdf.value = true;
  try {
    const url = tipo === 'orden-compra' 
      ? `/contrato-orden/pdf/orden-compra/${contratoId}`
      : `/contrato-orden/pdf/orden-servicio/${contratoId}`;
    
    const response = await axios.get(url, {
      responseType: 'blob'
    });
    
    // Crear URL del blob y descargar
    const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    const contrato = contratos.value.find(c => c.id === contratoId);
    const nombreArchivo = tipo === 'orden-compra' 
      ? `orden_compra_${contrato?.numero_documento || contratoId}.pdf`
      : `orden_servicio_${contrato?.numero_documento || contratoId}.pdf`;
    
    link.href = blobUrl;
    link.setAttribute('download', nombreArchivo);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(blobUrl);
    
    Swal.fire('Éxito', 'PDF generado correctamente', 'success');
  } catch (error) {
    console.error('Error al generar PDF:', error);
    Swal.fire('Error', error.response?.data?.message || 'No se pudo generar el PDF', 'error');
  } finally {
    generandoPdf.value = false;
  }
};

const formatNumber = (n) => new Intl.NumberFormat('es-BO', { minimumFractionDigits: 2 }).format(n);
const formatDate = (date) => date ? new Date(date).toLocaleDateString('es-BO') : 'N/A';
const formatTipoDocumento = (t) => ({ 'ORDEN_COMPRA':'Orden Compra','ORDEN_SERVICIO':'Orden Servicio','CONTRATO_ADMINISTRATIVO':'Contrato' }[t] || t);

onMounted(() => { fetchContratos(); });
</script>